/**
 * Component: Reviews Section
 * ------------------------------------------------------------------------------
 * Scripts for the shopify reviews section, loaded as an app block outside Vue.
 *
 */
import { rightArrow, leftArrow } from '~/helpers/svg-map'

/**
  * DOM selectors.
  */
 const selectors = {
   section: '[js-reviews-section="section"]',
   newReviewButton: '.spr-summary-actions-newreview',
 }

 /**
  * Create a reviews section object.
  */
 export default () => {

   /**
    * DOM node selectors.
    */
   const nodeSelectors = {
     section: document.querySelector(selectors.section),
   }

   /**
    * Initialise component.
    */
   function init() {
     setMutationObserver()
     addEventBusListeners()
   }

   /**
    * Watch for app content being injected.
    */
   function setMutationObserver() {
    const targetNode = nodeSelectors.section
    if (!targetNode) {
      return
    }

    const config = { attributes: true, childList: true, subtree: true }
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          const nodes = [...mutation.addedNodes]

          if (!nodes?.length) {
            continue
          }
          observer.disconnect()
          sectionLoaded()
          return
        }
      }
    }

    const observer = new MutationObserver(callback)
    observer.observe(targetNode, config)
   }

   /**
    * Adds event bus listeners.
    */
   async function addEventBusListeners() {
    await new Promise((resolve) => {
      const cnvsInterval = window.setInterval(() => {
        if (!cnvs?.EventBus?.listen) {
          return
        }

        window.clearInterval(cnvsInterval)
        resolve()
      }, 200)
    })

    cnvs.EventBus.listen('cnvs:product-updated', (update) => {
      handleProductUpdated(update)
    })
   }

   /**
    * Adds arrows to the pagination links instead of the text.
    */
   function handleProductUpdated(update) {
    const reviews = document.querySelector('#shopify-product-reviews')

    if (!reviews) {
      return
    }

    if (parseInt(reviews.dataset.id, 10) === update.response.id) {
      return
    }

    reviews.dataset.id = update.response.id
    const scripts = [...document.scripts]
    const productReviews = scripts.find(script => script.src.includes('productreviews'))

    load_js(productReviews.src)
   }

   /**
    * Reloads the shopify product reviews script.
    */
   function load_js(src) {
      const head = document.getElementsByTagName('head')[0]
      const reviewScripts = [...head.children].filter(element => element.src === src)
      reviewScripts.forEach(script => {
        script.parentNode.removeChild(script)
      })

      const script = document.createElement('script')
      script.src = src
      head.appendChild(script)

      /**
       * Re-initialise this script when the product updates.
       */
      init()
   }

   /**
    * Adds arrows to the pagination links instead of the text.
    */
   function addPaginationArrows() {
    const previous = document.querySelector('.spr-pagination-prev > a')
    const next = document.querySelector('.spr-pagination-next > a')

    if (previous) {
      previous.innerHTML = leftArrow
    }

    if (next) {
      next.innerHTML = rightArrow
    }
   }

   /**
    * Run all manipulation functions when the app content has been injected.
    */
   function sectionLoaded(setObserver = true) {
    const newReviewBtn = nodeSelectors.section.querySelector(selectors.newReviewButton)
    nodeSelectors.newReviewBtn = newReviewBtn

    addButtonClasses()
    addIconToButton()
    addPaginationArrows()

    if (!setObserver) {
      return
    }
    setReviewsObservationObserver()
   }

   /**
    * Adds another mutation observer to listener for pagination updates.
    */
   function setReviewsObservationObserver() {
      const targetNode = document.querySelector('.spr-reviews')
      if (!targetNode) {
        return
      }

      const config = { attributes: true, childList: true, subtree: true }

      const callback = (mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            addPaginationArrows()
            observer.disconnect()
            setReviewsObservationObserver()
            return
          }
        }
      }


      const observer = new MutationObserver(callback)
      observer.observe(targetNode, config)
   }

   /**
    * Make the new review link a button element.
    */
   function addButtonClasses() {
      if (!nodeSelectors.newReviewBtn) {
        return
      }

      nodeSelectors.newReviewBtn.classList.add('button')
   }

   /**
    * Adds an arrow to the new review button.
    */
   function addIconToButton() {
      if (!nodeSelectors.newReviewBtn) {
        return
      }

      nodeSelectors.newReviewBtn.insertAdjacentHTML('beforeend', rightArrow)
   }

   /**
    * Expose public interface.
    */
   return Object.freeze({
     init,
   })
 }
